import { useState, useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import type { Account, AccountType, CardBrand, Member } from '@zeni/shared'
import { CARD_BRANDS } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { useAmountFormat } from '../contexts/AmountUnitContext'
import { accountsApi, membersApi } from '../lib/api'
import { InstitutionCombobox, type Institution } from './InstitutionCombobox'
import { MemberCombobox } from './MemberCombobox'
import { CardBrandMark } from './CardBrandMark'
import { CARD_BRAND_LABELS } from '../lib/card-brand'
import { ACCOUNT_TYPE_KEYS } from '../lib/transfer'
import { useModalReset } from '../lib/use-modal-reset'
import type { TranslationKey } from '../i18n/translations'
import { btnAccent, btnDanger, btnPrimary, btnSecondary, modalTitle } from '../lib/ui'

interface AccountFormData {
  name: string
  type: AccountType
  memberId: string
  initialBalance: string
  creditLimit: string
  /** カードブランド。'' は未設定 */
  cardBrand: string
  paymentAccountId: string
  closingDay: string
  paymentDay: string
  paymentMonthOffset: string
  institution: string
  color: string
}

const BANK_TYPES: AccountType[] = ['checking', 'savings']

// 締め日・引き落とし日の選択肢。31 は「月末」の意味で使い、実際の日付は
// その月の末日に丸められる（2月末・30日までの月をまとめて扱うため）
const DAY_OPTIONS = Array.from({ length: 31 }, (_, i) => i + 1)

// 口座種別 → 金融機関マスタのカテゴリ。選んだ種別に合う金融機関だけを候補に出す
// （証券口座なのにクレジットカード会社が並ぶのを防ぐ）。
// cash / other は対応する業態が定まらないため絞り込まず、全件から選ばせる。
const INSTITUTION_CATEGORY_BY_TYPE: Partial<Record<AccountType, string>> = {
  checking: 'bank',
  savings: 'bank',
  credit_card: 'credit_card',
  investment: 'securities',
}

interface AccountModalProps {
  open: boolean
  initial?: Account | null
  allowedTypes: AccountType[]
  /** クレジットカードの引き落とし先として選択できる銀行口座（普通・当座） */
  bankAccounts?: Account[]
  defaultColor?: string
  titleAddKey?: TranslationKey
  titleEditKey?: TranslationKey
  /** 名前欄のラベル。クレジットカード画面のように「口座」と呼びたくない場合に差し替える */
  nameLabelKey?: TranslationKey
  namePlaceholderKey?: TranslationKey
  onClose: () => void
  onSaved: () => void
  /**
   * 渡すと編集時に完全削除ボタンを表示する。確認ダイアログはモーダル側で出すので
   * 実際の削除だけを行い、失敗時は throw する（モーダル内にエラーを表示する）。
   */
  onDelete?: (account: Account) => Promise<void>
  /**
   * 渡すと編集時にアーカイブ／アーカイブ解除ボタンを表示する。切り替えだけを行い、
   * 失敗時は throw する（モーダル内にエラーを表示する）。
   */
  onArchive?: (account: Account) => Promise<void>
  token: string
}

export function AccountModal({
  open,
  initial,
  allowedTypes,
  bankAccounts = [],
  defaultColor = '#6366f1',
  titleAddKey = 'accounts_modal_add',
  titleEditKey = 'accounts_modal_edit',
  nameLabelKey = 'accounts_form_name',
  namePlaceholderKey = 'accounts_form_name_placeholder',
  onClose,
  onSaved,
  onDelete,
  onArchive,
  token,
}: AccountModalProps) {
  const { t } = useLanguage()
  const defaultFormData: AccountFormData = {
    name: '',
    type: allowedTypes[0],
    memberId: '',
    initialBalance: '0',
    creditLimit: '',
    cardBrand: '',
    paymentAccountId: '',
    closingDay: '',
    paymentDay: '',
    paymentMonthOffset: '1',
    institution: '',
    color: defaultColor,
  }
  const [form, setForm] = useState<AccountFormData>(defaultFormData)
  // ユーザーが自分でカラーを触ったか。触っていない間だけ、選んだ金融機関の
  // コーポレートカラーで塗り替える（ユーザーの指定を勝手に上書きしないため）
  const colorTouchedRef = useRef(false)
  const [members, setMembers] = useState<Member[]>([])
  const [submitting, setSubmitting] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [archiving, setArchiving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useModalReset(open, initial?.id ?? null, () => {
    if (initial) {
      setForm({
        name: initial.name,
        type: initial.type,
        memberId: initial.memberId ?? '',
        initialBalance: String(initial.initialBalance),
        creditLimit: initial.creditLimit != null ? String(initial.creditLimit) : '',
        cardBrand: initial.cardBrand ?? '',
        paymentAccountId: initial.paymentAccountId ?? '',
        closingDay: initial.closingDay != null ? String(initial.closingDay) : '',
        paymentDay: initial.paymentDay != null ? String(initial.paymentDay) : '',
        paymentMonthOffset: String(initial.paymentMonthOffset ?? 1),
        institution: initial.institution ?? '',
        color: initial.color ?? defaultColor,
      })
    } else {
      setForm(defaultFormData)
    }
    colorTouchedRef.current = false
    setError(null)
  })

  // 所有者の候補は開くたびに取り直す。設定でメンバーを追加した直後でも
  // ページを再読み込みせずに選べるようにする
  useEffect(() => {
    if (!open || !token) return
    void membersApi.list(token).then((res) => setMembers(res.data)).catch(() => undefined)
  }, [open, token])

  const isCreditCard = form.type === 'credit_card'
  // 証券口座の残高は保有商品の評価額合計で上書きされるため、開始残高を持たせても
  // 次回の評価額更新で消える。編集させると誤解を招くので入力欄ごと出さない。
  const isSecurities = form.type === 'investment'
  const canEditInitialBalance = !isCreditCard && !isSecurities

  // 金融機関を選んだら、その金融機関のコーポレートカラーをカラーの初期値として入れる。
  // ユーザーが自分でカラーを触ったあとは、選び直しても上書きしない
  const handleInstitutionChange = (value: string, institution?: Institution) => {
    setForm((f) => ({
      ...f,
      institution: value,
      color: !colorTouchedRef.current && institution?.brandColor ? institution.brandColor : f.color,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    setError(null)
    try {
      const basePayload = {
        name: form.name,
        institution: form.institution || undefined,
        color: form.color || undefined,
      }
      // 更新では null を送って所有者の割り当てを外せるが、作成では未指定にする
      const memberIdForUpdate = form.memberId === '' ? null : form.memberId
      const memberIdForCreate = form.memberId || undefined
      if (initial) {
        const payload = isCreditCard
          ? {
              ...basePayload,
              memberId: memberIdForUpdate,
              creditLimit: form.creditLimit === '' ? null : parseFloat(form.creditLimit) || 0,
              cardBrand: form.cardBrand === '' ? null : (form.cardBrand as CardBrand),
              paymentAccountId: form.paymentAccountId === '' ? null : form.paymentAccountId,
              closingDay: form.closingDay === '' ? null : parseInt(form.closingDay, 10),
              paymentDay: form.paymentDay === '' ? null : parseInt(form.paymentDay, 10),
              paymentMonthOffset: parseInt(form.paymentMonthOffset, 10) || 0,
            }
          : canEditInitialBalance
            ? {
                ...basePayload,
                memberId: memberIdForUpdate,
                initialBalance: parseFloat(form.initialBalance) || 0,
              }
            : { ...basePayload, memberId: memberIdForUpdate }
        await accountsApi.update(token, initial.id, payload)
      } else {
        const createBasePayload = { ...basePayload, type: form.type }
        const payload = isCreditCard
          ? {
              ...createBasePayload,
              memberId: memberIdForCreate,
              creditLimit: form.creditLimit === '' ? undefined : parseFloat(form.creditLimit) || 0,
              cardBrand: form.cardBrand === '' ? undefined : (form.cardBrand as CardBrand),
              paymentAccountId: form.paymentAccountId || undefined,
              closingDay: form.closingDay === '' ? undefined : parseInt(form.closingDay, 10),
              paymentDay: form.paymentDay === '' ? undefined : parseInt(form.paymentDay, 10),
              paymentMonthOffset: parseInt(form.paymentMonthOffset, 10) || 0,
            }
          : canEditInitialBalance
            ? {
                ...createBasePayload,
                memberId: memberIdForCreate,
                balance: parseFloat(form.initialBalance) || 0,
              }
            : { ...createBasePayload, memberId: memberIdForCreate }
        await accountsApi.create(token, payload)
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_save_error'))
    } finally {
      setSubmitting(false)
    }
  }

  // 完全削除。取引が紐づく口座はサーバーが 409 で拒否するので、その理由をモーダル内に出す
  const handleDelete = async () => {
    if (!initial || !onDelete) return
    if (!confirm(t('accounts_delete_confirm').replace('{name}', initial.name))) return
    setDeleting(true)
    setError(null)
    try {
      await onDelete(initial)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_delete_error'))
    } finally {
      setDeleting(false)
    }
  }

  const handleArchive = async () => {
    if (!initial || !onArchive) return
    setArchiving(true)
    setError(null)
    try {
      await onArchive(initial)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common_update_error'))
    } finally {
      setArchiving(false)
    }
  }

  if (!open) return null

  const showBankGroup = allowedTypes.includes('checking') && allowedTypes.includes('savings')
  const otherTypes = allowedTypes.filter((t) => !(showBankGroup && BANK_TYPES.includes(t)))

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 p-5 dark:bg-gray-900">
        <h2 className={`${modalTitle} mb-4`}>
          {initial ? t(titleEditKey) : t(titleAddKey)}
        </h2>
        <form onSubmit={(e) => { void handleSubmit(e) }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
              {t(nameLabelKey)} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              placeholder={t(namePlaceholderKey)}
            />
          </div>
          {/* 種別は作成時にだけ決める。あとから変えると引き落とし先や保有商品が
              噛み合わなくなるため、編集では表示だけにする */}
          {!initial && allowedTypes.length > 1 ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_type')}</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  type: e.target.value as AccountType,
                  institution: '',
                  // 金融機関が外れるので、金融機関由来で入っていたカラーも既定色に戻す
                  color: colorTouchedRef.current ? f.color : defaultColor,
                }))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              >
                {showBankGroup && (
                  <optgroup label={t('account_type_bank_group')}>
                    <option value="checking">{t('account_type_checking')}</option>
                    <option value="savings">{t('account_type_savings')}</option>
                  </optgroup>
                )}
                {otherTypes.map((type) => (
                  <option key={type} value={type}>{t(ACCOUNT_TYPE_KEYS[type])}</option>
                ))}
              </select>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_type')}</label>
              <p className="text-sm text-gray-900 dark:text-gray-100">{t(ACCOUNT_TYPE_KEYS[form.type])}</p>
              {initial && allowedTypes.length > 1 && (
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('accounts_form_type_locked')}</p>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_institution')}</label>
            <InstitutionCombobox
              value={form.institution}
              onChange={handleInstitutionChange}
              token={token}
              placeholder={t('accounts_form_institution_placeholder')}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
              filterCategory={INSTITUTION_CATEGORY_BY_TYPE[form.type]}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_member')}</label>
            <MemberCombobox
              value={form.memberId}
              members={members}
              onChange={(memberId) => setForm((f) => ({ ...f, memberId }))}
              placeholder={t('accounts_form_member_none')}
              className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
            />
            {members.length === 0 && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">{t('accounts_form_member_hint')}</p>
            )}
          </div>
          {isCreditCard ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_card_brand')}</label>
                <div className="flex items-center gap-3">
                  <select
                    value={form.cardBrand}
                    onChange={(e) => setForm((f) => ({ ...f, cardBrand: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    <option value="">{t('accounts_form_card_brand_none')}</option>
                    {CARD_BRANDS.map((brand) => (
                      <option key={brand} value={brand}>{CARD_BRAND_LABELS[brand]}</option>
                    ))}
                  </select>
                  {/* 選んだブランドが券面でどう出るかをその場で見せる */}
                  {form.cardBrand && (
                    <span className="shrink-0 text-gray-700 dark:text-gray-200">
                      <CardBrandMark brand={form.cardBrand as CardBrand} className="h-6" />
                    </span>
                  )}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_credit_limit')}</label>
                <input
                  type="number"
                  value={form.creditLimit}
                  onChange={(e) => setForm((f) => ({ ...f, creditLimit: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  step="any"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_payment_account')}</label>
                <select
                  value={form.paymentAccountId}
                  onChange={(e) => setForm((f) => ({ ...f, paymentAccountId: e.target.value }))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                >
                  <option value="">{t('accounts_form_payment_account_none')}</option>
                  {bankAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name}{a.institution ? `（${a.institution}）` : ''}
                    </option>
                  ))}
                </select>
              </div>
              {/* 締め日と引き落とし日。ここが埋まっているカードだけ引き落とし予定を出せる */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_closing_day')}</label>
                  <select
                    value={form.closingDay}
                    onChange={(e) => setForm((f) => ({ ...f, closingDay: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    <option value="">{t('accounts_form_day_unset')}</option>
                    {DAY_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d === 31 ? t('accounts_form_day_end_of_month') : t('accounts_form_day_nth').replace('{day}', String(d))}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_payment_month')}</label>
                  <select
                    value={form.paymentMonthOffset}
                    onChange={(e) => setForm((f) => ({ ...f, paymentMonthOffset: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    <option value="0">{t('accounts_form_payment_month_same')}</option>
                    <option value="1">{t('accounts_form_payment_month_next')}</option>
                    <option value="2">{t('accounts_form_payment_month_after_next')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_payment_day')}</label>
                  <select
                    value={form.paymentDay}
                    onChange={(e) => setForm((f) => ({ ...f, paymentDay: e.target.value }))}
                    className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                  >
                    <option value="">{t('accounts_form_day_unset')}</option>
                    {DAY_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d === 31 ? t('accounts_form_day_end_of_month') : t('accounts_form_day_nth').replace('{day}', String(d))}</option>
                    ))}
                  </select>
                </div>
              </div>
              <p className="-mt-2 text-xs text-gray-400 dark:text-gray-500">{t('accounts_form_billing_cycle_hint')}</p>
            </>
          ) : canEditInitialBalance && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_balance')}</label>
              <input
                type="number"
                value={form.initialBalance}
                onChange={(e) => setForm((f) => ({ ...f, initialBalance: e.target.value }))}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                step="any"
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">{t('accounts_form_color')}</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={form.color}
                onChange={(e) => { colorTouchedRef.current = true; setForm((f) => ({ ...f, color: e.target.value })) }}
                className="h-9 w-16 rounded border border-gray-200 cursor-pointer dark:border-gray-600"
              />
              <input
                type="text"
                value={form.color}
                onChange={(e) => { colorTouchedRef.current = true; setForm((f) => ({ ...f, color: e.target.value })) }}
                className="border border-gray-200 rounded-lg px-3 py-2 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:focus:border-indigo-400"
                placeholder="#6366f1"
              />
            </div>
          </div>
          {error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}
          <div className="flex items-center justify-between gap-2 pt-2">
            <div className="flex flex-wrap gap-2">
              {initial && onArchive && (
                <button
                  type="button"
                  onClick={() => { void handleArchive() }}
                  disabled={archiving || deleting || submitting}
                  className={btnSecondary}
                >
                  {initial.isArchived ? t('accounts_unarchive') : t('accounts_archive')}
                </button>
              )}
              {initial && onDelete && (
                <button
                  type="button"
                  onClick={() => { void handleDelete() }}
                  disabled={deleting || archiving || submitting}
                  className={btnDanger}
                >
                  {deleting ? t('accounts_deleting') : t('common_delete')}
                </button>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                className={btnSecondary}
              >
                {t('common_cancel')}
              </button>
              <button
                type="submit"
                disabled={submitting || deleting || archiving}
                className={btnPrimary}
              >
                {submitting ? t('common_saving') : t('common_save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

function hexToRgb(hex: string): [number, number, number] | null {
  const cleaned = hex.replace('#', '')
  if (!/^[0-9a-fA-F]{6}$/.test(cleaned)) return null
  const v = parseInt(cleaned, 16)
  return [(v >> 16) & 0xff, (v >> 8) & 0xff, v & 0xff]
}

function relativeLuminance([r, g, b]: [number, number, number]): number {
  const channel = (c: number) => {
    const s = c / 255
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4)
  }
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b)
}

// 指定色を白（ratio > 0）／黒（ratio < 0）へ寄せた CSS 色を返す。
// 単色指定のカードに実物らしい陰影を付けるために使う
function shade(rgb: [number, number, number], ratio: number): string {
  const target = ratio >= 0 ? 255 : 0
  const k = Math.abs(ratio)
  const [r, g, b] = rgb.map((c) => Math.round(c + (target - c) * k))
  return `rgb(${r}, ${g}, ${b})`
}

// 色未設定のカードでも実物らしく見えるようにする既定色（スレート系）
const DEFAULT_CARD_RGB: [number, number, number] = [51, 65, 85]

// 「15日締め翌月10日払い」のような 1 行。締め日・引き落とし日が揃っているときだけ返す
function billingCycleLabelOf(account: Account, t: (key: TranslationKey) => string): string | null {
  if (account.closingDay == null || account.paymentDay == null) return null
  const dayLabel = (day: number) => day === 31
    ? t('accounts_form_day_end_of_month')
    : t('accounts_form_day_nth').replace('{day}', String(day))
  const monthKey = ['accounts_form_payment_month_same', 'accounts_form_payment_month_next', 'accounts_form_payment_month_after_next'] as const
  return t('accounts_card_billing_cycle')
    .replace('{closing}', dayLabel(account.closingDay))
    .replace('{month}', t(monthKey[account.paymentMonthOffset] ?? 'accounts_form_payment_month_next'))
    .replace('{payment}', dayLabel(account.paymentDay))
}

/** 口座種別ごとの既定色。色を設定していない口座でも、券面の色で種別を見分けられるようにする */
const ACCOUNT_TYPE_RGB: Partial<Record<AccountType, [number, number, number]>> = {
  checking: [37, 78, 150],
  savings: [15, 118, 110],
  investment: [91, 51, 168],
  cash: [161, 98, 7],
  other: [71, 85, 105],
}

/** 券面に載せる種別マーク。線画なので色は currentColor に従う */
function AccountTypeMark({ type, className }: { type: AccountType; className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {type === 'checking' && (
        <>
          <path d="M3 9.5 12 4.5l9 5" />
          <path d="M5.5 10.5v7M10 10.5v7M14 10.5v7M18.5 10.5v7" />
          <path d="M3.5 19.5h17" />
        </>
      )}
      {type === 'savings' && (
        <>
          <rect x="3.5" y="5" width="17" height="14" rx="2" />
          <circle cx="12" cy="12" r="3" />
          <path d="M12 9v6" />
        </>
      )}
      {type === 'investment' && (
        <>
          <path d="M4 17.5 9.5 11l3.5 3.5L20 7" />
          <path d="M15 7h5v5" />
        </>
      )}
      {type === 'cash' && (
        <>
          <rect x="3" y="7" width="18" height="10" rx="2" />
          <circle cx="12" cy="12" r="2.5" />
        </>
      )}
      {type !== 'checking' && type !== 'savings' && type !== 'investment' && type !== 'cash' && (
        <>
          <path d="M3 8.5A2.5 2.5 0 0 1 5.5 6H18a3 3 0 0 1 3 3v6a3 3 0 0 1-3 3H5.5A2.5 2.5 0 0 1 3 15.5z" />
          <path d="M16.5 12h2" />
        </>
      )}
    </svg>
  )
}

/**
 * 口座を実物のキャッシュカードに見立てて並べるタイル。
 * クレジットカード一覧（CreditCardTile）と同じ券面の作りにして、口座一覧も
 * 「カードが並んでいる」と一目で分かるようにする。券面には残高・種別・口座名という
 * 一覧で必要なものだけを載せ、設定値は編集モーダルに任せる
 */
export function AccountTile({
  account,
  onEdit,
  onCsvImport,
  transactionsHref,
}: {
  account: Account
  onEdit: (account: Account) => void
  /** 渡すとCSVインポートボタンを表示する。対応フォーマットがある口座だけ渡す */
  onCsvImport?: (account: Account) => void
  /** 渡すとカード本体がこの URL へのリンクになる（この口座で絞り込んだ取引一覧など） */
  transactionsHref?: string
}) {
  const { t } = useLanguage()
  const { formatCurrency } = useAmountFormat()
  const formattedBalance = formatCurrency(account.balance, account.currency)

  const rgb = (account.color ? hexToRgb(account.color) : null)
    ?? ACCOUNT_TYPE_RGB[account.type]
    ?? DEFAULT_CARD_RGB
  const isLightText = relativeLuminance(rgb) <= 0.5

  const primaryText = isLightText ? 'rgba(255,255,255,0.98)' : 'rgba(17,24,39,0.95)'
  const secondaryText = isLightText ? 'rgba(255,255,255,0.8)' : 'rgba(55,65,81,0.95)'
  const mutedText = isLightText ? 'rgba(255,255,255,0.62)' : 'rgba(75,85,99,0.85)'
  // マイナス残高（借越・建て替え）は券面の上でも気付けるようにする
  const alertText = isLightText ? 'rgba(254,202,202,1)' : 'rgba(185,28,28,1)'
  const pillBg = isLightText ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
  const glossStrong = isLightText ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.4)'
  const glossWeak = isLightText ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.22)'

  // 単色の指定色から、実物のカードのような陰影のあるグラデーションを作る
  const cardStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, ${shade(rgb, 0.2)} 0%, ${shade(rgb, 0)} 45%, ${shade(rgb, -0.28)} 100%)`,
  }

  const face = (
    <div
      className={`relative aspect-[1.586] w-full overflow-hidden rounded-2xl p-5 shadow-lg ring-1 ring-black/10 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:shadow-xl ${account.isArchived ? 'opacity-60' : ''}`}
      style={cardStyle}
    >
      {/* 実物のカードの光沢に見立てた装飾。情報は載せない */}
      <div className="pointer-events-none absolute -right-12 -top-20 h-56 w-56 rounded-full" style={{ backgroundColor: glossStrong }} />
      <div className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full" style={{ backgroundColor: glossWeak }} />
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <span
              className="inline-flex max-w-full items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ backgroundColor: pillBg, color: secondaryText }}
            >
              <AccountTypeMark type={account.type} className="h-3.5 w-3.5 shrink-0" />
              <span className="truncate">{t(ACCOUNT_TYPE_KEYS[account.type])}</span>
            </span>
            {account.isArchived && (
              <span
                className="rounded-full px-2 py-0.5 text-[11px] font-medium"
                style={{ backgroundColor: pillBg, color: secondaryText }}
              >
                {t('accounts_status_archived')}
              </span>
            )}
          </div>
          <div className="min-w-0 text-right">
            <p className="text-[11px]" style={{ color: mutedText }}>{t('accounts_card_balance')}</p>
            <p
              className="truncate text-xl font-bold leading-tight sm:text-2xl"
              style={{ color: account.balance < 0 ? alertText : primaryText }}
            >
              {formattedBalance}
            </p>
          </div>
        </div>

        {/* IC チップは実物と同じく左の中ほどに置く。クレジットカード（金色）と
            見分けが付くよう、キャッシュカードらしい銀色にする */}
        <div className="flex items-center">
          <div className="aspect-[5/4] w-[16%] min-w-8 shrink-0 rounded-md bg-gradient-to-br from-slate-100 to-slate-400 shadow-sm" />
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="whitespace-nowrap text-[10px] font-medium tracking-[0.18em]" style={{ color: mutedText }}>ACCOUNT NAME</p>
            <p className="truncate text-base font-semibold group-hover:underline" style={{ color: primaryText }}>{account.name}</p>
          </div>
          {/* 実物のカードと同じく、金融機関名は右下に置く。名前が長くても口座名を
              潰さないよう、右側は半分までに収めて溢れは省略する */}
          {account.institution && (
            <p className="max-w-[50%] truncate text-sm font-semibold tracking-wide" style={{ color: secondaryText }}>
              {account.institution}
            </p>
          )}
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {transactionsHref ? (
        <Link to={transactionsHref} className="group block" title={t('accounts_card_view_transactions')}>
          {face}
        </Link>
      ) : face}
      <div className="mt-3 flex gap-2 px-1">
        <button
          onClick={() => onEdit(account)}
          className={`${btnAccent} flex-1`}
        >
          {t('common_edit')}
        </button>
        {onCsvImport && (
          <button
            onClick={() => onCsvImport(account)}
            className={`${btnSecondary} flex-1`}
          >
            {t('accounts_csv_import')}
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * クレジットカードを実物のカードに見立てて並べるタイル。
 * 一覧では「どのカードをどれだけ使ったか」がひと目で分かればよいので、
 * カード面には利用額と利用率だけを載せ、細かい設定値はカードの下に添える
 */
export function CreditCardTile({
  account,
  onEdit,
  onCsvImport,
  paymentAccountName,
  transactionsHref,
}: {
  account: Account
  onEdit: (account: Account) => void
  /** 渡すとCSVインポートボタンを表示する。対応フォーマットがあるカードだけ渡す */
  onCsvImport?: (account: Account) => void
  /** 引き落とし先銀行口座名（表示用。呼び出し側で解決して渡す） */
  paymentAccountName?: string
  /** 渡すとカード本体がこの URL へのリンクになる（このカードで絞り込んだ取引一覧など） */
  transactionsHref?: string
}) {
  const { t } = useLanguage()
  const { formatCurrency } = useAmountFormat()
  // カードの残高は負債なのでマイナスで持つが、カード上では「いくら使ったか」を
  // 素直に読めるよう符号を反転してプラスで出す
  const usedAmount = -account.balance
  const formattedUsed = formatCurrency(usedAmount, account.currency)
  const formattedCreditLimit = account.creditLimit != null
    ? formatCurrency(account.creditLimit, account.currency)
    : null
  // 利用可能枠に対する利用率。枠が未設定・0 のカードは出せない
  const utilization = account.creditLimit != null && account.creditLimit > 0
    ? (usedAmount / account.creditLimit) * 100
    : null
  const formattedAvailable = account.creditLimit != null
    ? formatCurrency(account.creditLimit - usedAmount, account.currency)
    : null
  const billingCycleLabel = billingCycleLabelOf(account, t)
  // 残高がプラスになるのは利用明細の取り込み漏れのサイン（AccountCard と同じ理由）
  const isOverpaidCard = account.balance > 0

  const rgb = (account.color ? hexToRgb(account.color) : null) ?? DEFAULT_CARD_RGB
  const isLightText = relativeLuminance(rgb) <= 0.5

  const primaryText = isLightText ? 'rgba(255,255,255,0.98)' : 'rgba(17,24,39,0.95)'
  const secondaryText = isLightText ? 'rgba(255,255,255,0.8)' : 'rgba(55,65,81,0.95)'
  const mutedText = isLightText ? 'rgba(255,255,255,0.62)' : 'rgba(75,85,99,0.85)'
  const alertText = isLightText ? 'rgba(254,202,202,1)' : 'rgba(185,28,28,1)'
  const barTrack = isLightText ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.12)'
  // 枠を使い切りかけているカードは、バーの色でも気付けるようにする
  const barFill = utilization != null && utilization >= 80
    ? alertText
    : (isLightText ? 'rgba(255,255,255,0.9)' : 'rgba(17,24,39,0.75)')
  const glossStrong = isLightText ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.4)'
  const glossWeak = isLightText ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.22)'

  // 単色の指定色から、実物のカードのような陰影のあるグラデーションを作る
  const cardStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(135deg, ${shade(rgb, 0.2)} 0%, ${shade(rgb, 0)} 45%, ${shade(rgb, -0.28)} 100%)`,
  }

  const face = (
    <div
      className={`relative aspect-[1.586] w-full overflow-hidden rounded-2xl p-5 shadow-lg ring-1 ring-black/10 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:shadow-xl ${account.isArchived ? 'opacity-60' : ''}`}
      style={cardStyle}
    >
      {/* 実物のカードの光沢に見立てた装飾。情報は載せない */}
      <div className="pointer-events-none absolute -right-12 -top-20 h-56 w-56 rounded-full" style={{ backgroundColor: glossStrong }} />
      <div className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full" style={{ backgroundColor: glossWeak }} />
      <div className="relative flex h-full flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          {account.isArchived ? (
            <span
              className="rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{ backgroundColor: barTrack, color: secondaryText }}
            >
              {t('accounts_status_archived')}
            </span>
          ) : <span />}
          <div className="min-w-0 text-right">
            <p className="text-[11px]" style={{ color: mutedText }}>{t('credit_cards_used')}</p>
            <p
              className="truncate text-xl font-bold leading-tight sm:text-2xl"
              style={{ color: isOverpaidCard ? alertText : primaryText }}
            >
              {formattedUsed}
            </p>
          </div>
        </div>

        {/* IC チップは実物と同じく左の中ほどに置く。利用率のバーはその右に並べる
            （枠が未設定でチップだけになっても、上下の間隔は変わらない） */}
        <div className="flex items-center gap-3">
          {/* 実物のチップは券面の横幅の 15% ほど。カードの大きさに追従させる */}
          <div className="aspect-[5/4] w-[16%] min-w-8 shrink-0 rounded-md bg-gradient-to-br from-amber-200 to-amber-500 shadow-sm" />
          {utilization != null && (
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2 text-[11px]" style={{ color: secondaryText }}>
                <span className="truncate">{t('credit_cards_utilization')}: {utilization.toFixed(1)}%</span>
                <span className="truncate">{t('credit_cards_available')}: {formattedAvailable}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full" style={{ backgroundColor: barTrack }}>
                <div
                  className="h-full rounded-full"
                  style={{ width: `${Math.min(100, Math.max(0, utilization))}%`, backgroundColor: barFill }}
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="whitespace-nowrap text-[10px] font-medium tracking-[0.18em]" style={{ color: mutedText }}>CARD NAME</p>
            <p className="truncate text-base font-semibold group-hover:underline" style={{ color: primaryText }}>{account.name}</p>
          </div>
          {/* 実物のカードと同じく、国際ブランドは右下に置く。金融機関名が長くても
              カード名を潰さないよう、右側は半分までに収めて溢れは省略する */}
          <div className="flex min-w-0 max-w-[50%] flex-col items-end gap-1">
            {account.institution && (
              <p className="max-w-full truncate text-sm font-semibold tracking-wide" style={{ color: secondaryText }}>{account.institution}</p>
            )}
            {account.cardBrand && (
              <span className="flex" style={{ color: primaryText }}>
                <CardBrandMark brand={account.cardBrand} className="h-5 sm:h-6" />
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      {transactionsHref ? (
        <Link to={transactionsHref} className="group block" title={t('accounts_card_view_transactions')}>
          {face}
        </Link>
      ) : face}
      <div className="mt-3 space-y-0.5 px-1 text-xs text-gray-500 dark:text-gray-400">
        {/* 利用可能枠・締め日・引き落とし先は未設定でも行を残す。行ごと消えると設定漏れに気付けない */}
        <p>
          {t('accounts_form_credit_limit')}: {formattedCreditLimit ?? <span className="text-gray-400 dark:text-gray-500">{t('common_not_set')}</span>}
        </p>
        <p>
          {billingCycleLabel ?? (
            <>
              {t('accounts_card_billing_cycle_label')}: <span className="text-gray-400 dark:text-gray-500">{t('common_not_set')}</span>
            </>
          )}
        </p>
        <p>
          {t('accounts_form_payment_account')}: {paymentAccountName || <span className="text-gray-400 dark:text-gray-500">{t('common_not_set')}</span>}
        </p>
        {isOverpaidCard && (
          <p className="text-amber-700 dark:text-amber-500">&#9888; {t('accounts_card_overpaid_warning')}</p>
        )}
      </div>
      <div className="mt-3 flex gap-2 px-1">
        <button
          onClick={() => onEdit(account)}
          className={`${btnAccent} flex-1`}
        >
          {t('common_edit')}
        </button>
        {onCsvImport && (
          <button
            onClick={() => onCsvImport(account)}
            className={`${btnSecondary} flex-1`}
          >
            {t('accounts_csv_import')}
          </button>
        )}
      </div>
    </div>
  )
}
